// src/logic/streamGenerator.js
import {
  REGION_POLICY,
  VERTICAL_POLICY,
  MESSAGE_LIBRARY,
  STREAM_BLUEPRINTS,
  DEFAULT_SEGMENTS,
} from "../data/streamPolicies";

// Helper to create deterministic IDs
const generateStreamID = (region, vertical, persona, stage) => {
  const r = (region || "GLO").substring(0, 3).toUpperCase();
  const v = (vertical || "GEN")
    .replace(/\s+/g, "")
    .substring(0, 4)
    .toUpperCase();
  const p = (persona || "ALL")
    .replace(/\s+/g, "")
    .substring(0, 3)
    .toUpperCase();
  const s = (stage || "AWR").substring(0, 3).toUpperCase();
  return `${r}_${v}_${p}_${s}`;
};

export const generateStreamFromSegment = (segment) => {
  // 1. Resolve Policies
  const regionPolicy = REGION_POLICY[segment.region] || REGION_POLICY.Default;
  const verticalPolicy =
    VERTICAL_POLICY[segment.vertical] || VERTICAL_POLICY.Default;

  // 2. Select Blueprint based on Stage (Default to Awareness)
  const stageKey = segment.stage || "Awareness";
  const blueprint = STREAM_BLUEPRINTS[stageKey] || STREAM_BLUEPRINTS.Awareness;

  // 3. Calculate Timing & Construct Touches
  const cadence = regionPolicy.cadenceDays;

  const touches = blueprint.map((step, index) => {
    const template = MESSAGE_LIBRARY[step.template];
    const day = Math.round(step.dayOffsetMultiplier * cadence);

    // Merge template with dynamic content
    return {
      step_id: `${String(index + 1).padStart(2, "0")}`,
      day: day,
      ...template,
      subject: template.marketer?.subjectLines?.[0] || "Subject Placeholder", // Use first subject option
      asset_focus: step.template.includes("context")
        ? verticalPolicy.theme
        : step.template.includes("proof")
        ? verticalPolicy.asset2
        : "N/A",
      is_conditional: step.conditional || false,
      // Pass full advisory guide
      advisory_guide: {
        marketer: template.marketer,
        mops: template.mops,
      },
    };
  });

  // 4. Generate Ops Rules
  const opsRules = {
    wait_step_duration: regionPolicy.waitStep,
    exclusion_rules: ["Opportunity Open", "Unsubscribed"],
    scoring_model: "MQL_v2",
    routing_trigger: "Score > 60 AND Region Match",
    intent_guardrails: [
      "completed_choice_logic",
      "visited_pricing_or_demo_page",
      "downloaded_business_case",
      "score >= 60",
    ],
  };

  // 5. Return The Complete Stream Object
  return {
    id: generateStreamID(
      segment.region,
      segment.vertical,
      segment.persona,
      segment.stage
    ),
    name: `${segment.region} | ${segment.vertical} | ${segment.persona} | ${segment.stage}`,
    status: "Draft",
    type: "Generated", // Flag to distinguish from legacy
    strategy: {
      cadence: regionPolicy.cadenceDays + " Days",
      tone: regionPolicy.tone,
      buying_question: regionPolicy.buyerQuestion,
    },
    stats: { leads: "0 (Pending)", engagement: "0%" },
    touches: touches,
    ops_rules: opsRules,
    ops_checklist: [
      `Configure Wait Step: ${opsRules.wait_step_duration}`,
      `Verify Content Theme: ${verticalPolicy.theme}`,
      `Confirm Exclusion: Opportunity Open`,
      `Set Intent Threshold: Score > 60`,
    ],
  };
};

export const buildStreamsFromSegments = (segments) => {
  // Use Default Segments if none provided
  const effectiveSegments =
    segments && segments.length > 0 ? segments : DEFAULT_SEGMENTS;
  return effectiveSegments.map((seg) => generateStreamFromSegment(seg));
};
